import usePhoneNumber from "./usePhoneNumber";

// Mirrors the backend PhoneNumberValidationHelper customer message (Issue-41587).
const INVALID_PHONE_MESSAGE = "Ungültige Telefonnummer. Bitte verwenden Sie das internationale Format (z.B. +49...)";
const REQUIRED_PHONE_MESSAGE = "Telefonnummer wird benötigt";

/**
 * Form helper around usePhoneNumber that centralizes phone-number submit gating, so every form
 * enforces the same rules as the backend (Issue-41587) and renders the same German messages.
 *
 * Spread `inputProps` onto the shared PhoneNumberInput, read `value` (E.164) for the payload, and
 * use `validForSubmit` to decide whether to block submission. Pass `submitted=true` once the user
 * has attempted to submit so the inline error only appears after a submit attempt.
 *
 * Gating policy (Issue-41587): create flows block on an invalid number; edit flows may warn-only so
 * a user with a previously-saved invalid number is not locked out of unrelated edits. Both cases use
 * the same `validForSubmit`/`errorMessage` here — the caller chooses whether to block or warn.
 *
 * @param initialPhoneNumber the existing E.164 number (for edit flows)
 * @param options.required   when true, an empty number is invalid for submit
 * @param options.submitted  when true, the inline validation error is shown
 */
const usePhoneField = (initialPhoneNumber = "", { required = false, submitted = false } = {}) => {
    const {
        phoneCountryCode,
        phoneLocal,
        setPhoneCountryCode,
        setPhoneLocal,
        getFullPhoneNumber,
        phoneNumberValid,
    } = usePhoneNumber(initialPhoneNumber);

    const value = getFullPhoneNumber();
    const isEmpty = !value;
    const validForSubmit = phoneNumberValid && (!required || !isEmpty);

    let errorMessage = "";
    if (!phoneNumberValid) {
        errorMessage = INVALID_PHONE_MESSAGE;
    } else if (required && isEmpty) {
        errorMessage = REQUIRED_PHONE_MESSAGE;
    }

    const inputProps = {
        phoneCountryCode,
        phoneLocal,
        onCountryCodeChange: setPhoneCountryCode,
        onPhoneLocalChange: setPhoneLocal,
        required,
        error: submitted ? errorMessage : "",
    };

    return {
        inputProps,
        value,
        isEmpty,
        valid: phoneNumberValid,
        validForSubmit,
        errorMessage,
        getFullPhoneNumber,
    };
};

export default usePhoneField;
